import { z } from "zod";
import { API_BASE_URL } from "./config";
import {
  companyNameSchema,
  contactMessageSchema,
  locationSchema,
  optionalMessageSchema,
  personNameSchema,
  phoneSchema,
  quoteMessageSchema,
  requirementTextSchema,
  shortNameSchema,
} from "./formValidators";

export {
  sanitizePhoneInput,
  zodFieldErrors,
} from "./formValidators";

export const CONTACT_SUBJECTS = [
  "Equipment Purchase",
  "Request Quote",
  "Maintenance",
  "Spare Parts",
  "Operator Training",
  "Other",
] as const;

export const CONTACT_METHODS = ["Email", "Phone", "WhatsApp"] as const;

const enquiryItemSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  model: z.string().min(1),
  image: z.string().url("Invalid product image"),
  quantity: z.number().int().min(1).max(99),
  category: z.string().optional(),
});

export const contactFormSchema = z.object({
  fullName: personNameSchema,
  email: z.string().trim().email("Enter a valid email").max(255),
  phone: phoneSchema,
  subject: z.enum(CONTACT_SUBJECTS),
  message: contactMessageSchema,
});

export const enquiryFormSchema = z.object({
  fullName: personNameSchema,
  company: companyNameSchema,
  email: z.string().trim().email("Enter a valid email").max(255),
  phone: phoneSchema,
  location: locationSchema,
  requirement: requirementTextSchema,
  message: optionalMessageSchema,
  contactMethod: z.enum(CONTACT_METHODS),
  items: z.array(enquiryItemSchema).min(1, "Add at least one item to your cart"),
});

export const quoteFormSchema = z.object({
  name: shortNameSchema,
  phone: phoneSchema,
  equipment: z.string().trim().min(1, "Equipment is required").max(100),
  message: quoteMessageSchema,
});

export type ContactFormRequest = z.infer<typeof contactFormSchema>;
export type EnquiryFormRequest = z.infer<typeof enquiryFormSchema>;
export type QuoteFormRequest = z.infer<typeof quoteFormSchema>;

export interface ApiResponse {
  success: boolean;
  message: string;
  data?: unknown;
  errors?: Array<{ field: string; message: string }>;
}

async function postForm<T>(path: string, body: T): Promise<ApiResponse> {
  try {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    let data: ApiResponse;
    try {
      data = (await res.json()) as ApiResponse;
    } catch {
      return {
        success: false,
        message: res.ok
          ? "Invalid response from server"
          : `Request failed (${res.status})`,
      };
    }

    if (!res.ok && data.success !== false) {
      return {
        success: false,
        message: data.message || `Request failed (${res.status})`,
        errors: data.errors,
      };
    }

    return data;
  } catch {
    return {
      success: false,
      message:
        "Unable to connect. Check your connection or try again in a moment.",
    };
  }
}

export function submitContactForm(data: ContactFormRequest) {
  return postForm("/forms/contact", data);
}

export function submitEnquiryForm(data: EnquiryFormRequest) {
  return postForm("/forms/enquiry", data);
}

export function submitQuoteForm(data: QuoteFormRequest) {
  return postForm("/forms/quote", data);
}

/** Turn relative image paths into absolute URLs for Zod url validation. */
export function absolutizeImageUrl(image: string, origin?: string): string {
  if (/^https?:\/\//i.test(image)) return image;
  const base =
    origin ??
    (typeof window !== "undefined" ? window.location.origin : "");
  if (!base) return image;
  const path = image.startsWith("/") ? image : `/${image}`;
  return `${base.replace(/\/$/, "")}${path}`;
}
